import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  TestTube,
  X,
  CheckCircle,
  XCircle,
  Globe
} from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const ApiConfigs = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [testingConfig, setTestingConfig] = useState(null);

  const { data: configs, isLoading, refetch } = useQuery(
    'api-configs',
    adminAPI.getApiConfigs,
    {
      select: (response) => response.data.data.configs
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  const handleCreate = () => {
    setEditingConfig(null);
    reset({
      name: '',
      base_url: '',
      api_key: '',
      headers: '{}',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEdit = async (config) => {
    try {
      const response = await adminAPI.getApiConfig(config.id);
      const configData = response.data.data.config;
      
      setEditingConfig(configData);
      reset({
        name: configData.name,
        base_url: configData.base_url,
        api_key: configData.api_key,
        headers: typeof configData.headers === 'object' 
          ? JSON.stringify(configData.headers, null, 2)
          : configData.headers || '{}',
        status: configData.status
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load configuration');
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this API configuration?')) {
      return;
    }

    try {
      await adminAPI.deleteApiConfig(configId);
      toast.success('API configuration deleted successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to delete API configuration');
    }
  };

  const handleTest = async (configId) => {
    setTestingConfig(configId);
    try {
      const response = await adminAPI.testApiConfig(configId);
      if (response.data.success) {
        toast.success('API test successful');
      } else {
        toast.error('API test failed: ' + response.data.error);
      }
    } catch (error) {
      toast.error('API test failed');
    } finally {
      setTestingConfig(null);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Validate JSON headers
      let headers = {};
      if (data.headers && data.headers.trim()) {
        try {
          headers = JSON.parse(data.headers);
        } catch (error) {
          toast.error('Invalid JSON in headers field');
          return;
        }
      }

      const configData = {
        ...data,
        headers: Object.keys(headers).length > 0 ? headers : null
      };

      if (editingConfig) {
        await adminAPI.updateApiConfig(editingConfig.id, configData);
        toast.success('API configuration updated successfully');
      } else {
        await adminAPI.createApiConfig(configData);
        toast.success('API configuration created successfully');
      }

      setShowModal(false);
      setEditingConfig(null);
      refetch();
    } catch (error) {
      toast.error('Failed to save API configuration');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">API Configurations</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage external API endpoints and authentication
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Configuration
        </button>
      </div>

      {/* Configurations List */}
      {configs && configs.length > 0 ? (
        <div className="grid gap-6">
          {configs.map((config) => (
            <div key={config.id} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Globe className="h-8 w-8 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {config.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {config.base_url}
                      </p>
                      <div className="flex items-center mt-2 space-x-4">
                        <span className={`badge ${
                          config.status === 'active' ? 'badge-success' : 'badge-secondary'
                        }`}>
                          {config.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          Created: {new Date(config.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleTest(config.id)}
                      disabled={testingConfig === config.id}
                      className="btn btn-outline btn-sm"
                      title="Test API Connection"
                    >
                      {testingConfig === config.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <TestTube className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(config)}
                      className="btn btn-outline btn-sm"
                      title="Edit Configuration"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(config.id)}
                      className="btn btn-outline btn-sm text-red-600 hover:text-red-700"
                      title="Delete Configuration"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No API configurations</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first API configuration.
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreate}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Configuration
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingConfig ? 'Edit API Configuration' : 'Add API Configuration'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Name */}
                    <div className="form-group">
                      <label className="form-label">Configuration Name *</label>
                      <input
                        {...register('name', { required: 'Name is required' })}
                        type="text"
                        className={`input ${errors.name ? 'border-red-300' : ''}`}
                        placeholder="e.g., Main API"
                      />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>

                    {/* Base URL */}
                    <div className="form-group">
                      <label className="form-label">Base URL *</label>
                      <input
                        {...register('base_url', { 
                          required: 'Base URL is required',
                          pattern: {
                            value: /^https?:\/\/.+/,
                            message: 'Must be a valid URL starting with http:// or https://'
                          }
                        })}
                        type="url"
                        className={`input ${errors.base_url ? 'border-red-300' : ''}`}
                        placeholder="https://api.example.com"
                      />
                      {errors.base_url && <p className="form-error">{errors.base_url.message}</p>}
                    </div>

                    {/* API Key */}
                    <div className="form-group">
                      <label className="form-label">API Key *</label>
                      <input
                        {...register('api_key', { required: 'API key is required' })}
                        type="password"
                        className={`input ${errors.api_key ? 'border-red-300' : ''}`}
                        placeholder="Enter API key"
                      />
                      {errors.api_key && <p className="form-error">{errors.api_key.message}</p>}
                    </div>

                    {/* Headers */}
                    <div className="form-group">
                      <label className="form-label">Additional Headers (JSON)</label>
                      <textarea
                        {...register('headers', {
                          validate: (value) => {
                            if (!value || value.trim() === '') return true;
                            try {
                              JSON.parse(value);
                              return true;
                            } catch {
                              return 'Invalid JSON format';
                            }
                          }
                        })}
                        className={`textarea font-mono text-sm ${errors.headers ? 'border-red-300' : ''}`}
                        rows={4}
                        placeholder='{"Content-Type": "application/json"}'
                      />
                      {errors.headers && <p className="form-error">{errors.headers.message}</p>}
                      <p className="form-help">
                        Optional JSON object with additional HTTP headers
                      </p>
                    </div>

                    {/* Status */}
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="input">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {editingConfig ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiConfigs;
